"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.facilityRoutingPreviewInputSchema = exports.facilityRoutingInputSchema = exports.facilityRoutingSchema = exports.routingBehaviourSchema = void 0;
/**
 * `RPM-036` — a building's routing profile: its behaviour code, text preference, hold, instruction to
 * the nurse and summary schedule, read and changed on the routing screen (`J-225`).
 *
 * THE BEHAVIOUR CODE SELECTS ROWS OF ONE FIXED TEMPLATE (`03-RPM-SCHEMA.md` §4.2.6, §10.1). A building
 * with no code, or one held for review, resolves its alerts `UNMET` (`RPM-035`). Changing the code, or
 * holding or releasing the building, changes where a critical goes, so it needs a stated reason; the
 * reason goes to `admin_change_events.stated_reason` as a zone change's does (`RPM-028`).
 *
 * THE INSTRUCTION IS SHOWN VERBATIM AND NEVER PARSED (`CD-4`): it is what the nurse reads beneath the
 * Facility control on the entry form.
 */
const zod_1 = require("zod");
const rpm_blast_radius_js_1 = require("./rpm-blast-radius.js");
const rpm_enums_js_1 = require("./db/rpm-enums.js");
/** The column's own enum, so the API cannot accept a code the database refuses. */
exports.routingBehaviourSchema = zod_1.z.enum(rpm_enums_js_1.routingBehavior.enumValues);
/** A local time of day, `HH:MM`, in the building's operative zone. */
const LOCAL_TIME = zod_1.z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, {
    message: "A time is HH:MM on a 24-hour clock, such as 07:30.",
});
/** The profile as the routing screen reads it. `null` behaviour is "not set up". */
exports.facilityRoutingSchema = zod_1.z.object({
    facilityId: zod_1.z.string().min(1).max(20),
    routingBehavior: exports.routingBehaviourSchema.nullable(),
    textPreferred: zod_1.z.boolean(),
    routingHoldReason: zod_1.z.string().nullable(),
    routingNotes: zod_1.z.string().nullable(),
    dailySummaryTimes: zod_1.z.array(LOCAL_TIME),
    weeklySummaryWeekday: zod_1.z.number().int().min(0).max(6).nullable(),
    weeklySummaryTime: LOCAL_TIME.nullable(),
    /** False until the building's first save: a building with no row is not set up. */
    exists: zod_1.z.boolean(),
});
/**
 * The whole profile, as the screen sends it. `statedReason` is required when the behaviour code or
 * the hold changes; the server decides that against the row it holds, never against what the screen
 * believed it was.
 */
exports.facilityRoutingInputSchema = zod_1.z
    .strictObject({
    routingBehavior: exports.routingBehaviourSchema.nullable(),
    textPreferred: zod_1.z.boolean(),
    routingHoldReason: zod_1.z.string().trim().max(500).nullable(),
    routingNotes: zod_1.z.string().max(2000).nullable(),
    dailySummaryTimes: zod_1.z.array(LOCAL_TIME).max(24),
    weeklySummaryWeekday: zod_1.z.number().int().min(0).max(6).nullable(),
    weeklySummaryTime: LOCAL_TIME.nullable(),
    statedReason: zod_1.z.string().trim().min(1).max(500).optional(),
    /**
     * The blast radius the confirm showed before a behaviour or hold change (`RPM-092` row 2,
     * `RPM-060`, `D-1544`), recorded with the change as it arrived. Optional on the wire because
     * the instruction, text preference and summary schedule change without a confirm.
     */
    blastRadiusShown: rpm_blast_radius_js_1.blastRadiusShownSchema.optional(),
})
    .refine((v) => (v.weeklySummaryWeekday === null) === (v.weeklySummaryTime === null), {
    message: "The weekly summary has a day and a time, or neither.",
    path: ["weeklySummaryTime"],
});
/**
 * The body of `POST /api/rpm/v1/admin/facilities/:facilityId/routing/preview` (`RPM-092` row 2,
 * `J-296`): the behaviour and hold the change would set. The count is the building's, as a zone
 * change's is: who a critical goes to changes for every alert at it.
 */
exports.facilityRoutingPreviewInputSchema = zod_1.z
    .strictObject({
    routingBehavior: exports.routingBehaviourSchema.nullable(),
    routingHoldReason: zod_1.z.string().trim().max(500).nullable(),
})
    .meta({ description: "The behaviour and hold a routing change would set" });
//# sourceMappingURL=rpm-facility-routing.js.map